import { GLOBAL_WILDCARD } from "@/features/rbac/permissions/catalog";
import type { PermissionOrWildcard } from "@/features/rbac/permissions/catalog";

/**
 * Roles del sistema: la semilla que reproduce el comportamiento que tenía la
 * aplicación cuando la autorización se basaba en `users.permission_admin`.
 *
 * Los ids son IDÉNTICOS a los diez valores de `permission_admin`. Eso es
 * deliberado: convierte el resolver de compatibilidad en una búsqueda directa,
 * sin tabla de traducción, y hace que editar el rol `support` desde la UI
 * afecte de inmediato a los usuarios que todavía no tienen asignación propia.
 *
 * Los conjuntos de abajo se derivaron auditando los gates que existían: los
 * arrays de rutas de `App.tsx`, los filtros de navegación de `Layout.tsx`,
 * `servers/utils/permissions.ts` y las condiciones inline de los componentes.
 */

/** Los diez valores heredados de `users.permission_admin`. */
export const LEGACY_ROLE_IDS = [
  "admin",
  "center_admin",
  "admin_it",
  "imaging",
  "laboratory",
  "bionalista",
  "users",
  "administration",
  "support",
  "deploy",
] as const;

export type LegacyRoleId = (typeof LEGACY_ROLE_IDS)[number];

export interface SystemRoleDef {
  readonly id: LegacyRoleId;
  readonly name: string;
  readonly description: string;
  readonly permissions: readonly PermissionOrWildcard[];
}

/**
 * El panel principal y las métricas del visor no tenían ningún gate: bastaba
 * con estar autenticado. Se concede a todos los roles del sistema para
 * reproducir ese comportamiento.
 */
const DASHBOARD_BASE: readonly PermissionOrWildcard[] = [
  "dashboard:view",
  "dashboard:visor_view",
];

/**
 * Gestión de cuentas ajenas: editar el perfil, restablecer la contraseña,
 * verificar el correo y habilitar o deshabilitar el acceso.
 *
 * Nota histórica: el listado de escritorio (`users-columns.tsx`) concedía esto
 * a admin/center_admin/support/users, mientras que la hoja de acciones móvil
 * (`UserActionsBottomSheet.tsx`) además incluía admin_it. Se toma la unión de
 * ambos para no quitarle accesos a nadie al desplegar.
 */
const ACCOUNT_MANAGEMENT: readonly PermissionOrWildcard[] = [
  "users:list",
  "users:view",
  "users:edit",
  "users:toggle_active",
  "users:reset_password_email",
  "users:set_password",
  "users:verify_email",
  "users:resend_verification",
];

export const SYSTEM_ROLES = [
  {
    id: "admin",
    name: "Administrador",
    description:
      "Acceso total a la aplicación. No se puede eliminar ni retirarle el permiso total: es el respaldo que garantiza que siempre exista alguien capaz de gestionar los roles.",
    permissions: [GLOBAL_WILDCARD],
  },
  {
    id: "center_admin",
    name: "Administrador de centro",
    description:
      "Acceso total, equivalente a Administrador. Se conserva así para reproducir el comportamiento anterior; si se quiere acotar, se edita desde esta misma pantalla.",
    permissions: [GLOBAL_WILDCARD],
  },
  {
    id: "admin_it",
    name: "Administrador TI",
    description:
      "Infraestructura de centros, configuración de plataforma e instalaciones, además de la gestión de cuentas.",
    permissions: [
      ...DASHBOARD_BASE,
      ...ACCOUNT_MANAGEMENT,
      "servers:*",
      "settings:*",
      "installations:*",
    ],
  },
  {
    id: "support",
    name: "Soporte",
    description:
      "Atención al cliente: cuentas, estudios de imagen, laboratorio, bioanálisis y la ficha de infraestructura de los centros.",
    permissions: [
      ...DASHBOARD_BASE,
      ...ACCOUNT_MANAGEMENT,
      "imagings:*",
      "laboratory:view",
      "bioanalysis:view",
      "servers:view",
      "servers:infra_edit",
      "servers:hosts_manage",
      "servers:software_manage",
      "servers:report_view",
      "servers:report_export",
    ],
  },
  {
    id: "imaging",
    name: "Imagenología",
    description:
      "Trabajo completo sobre los estudios de imagen. No incluye el listado de usuarios: la ruta /users nunca lo permitió, aunque el menú lateral sí lo mostrara y llevara a un enlace muerto.",
    permissions: [...DASHBOARD_BASE, "imagings:*"],
  },
  {
    id: "users",
    name: "Gestión de usuarios",
    description: "Alta y mantenimiento de las cuentas de la plataforma médica.",
    permissions: [...DASHBOARD_BASE, ...ACCOUNT_MANAGEMENT],
  },
  {
    id: "administration",
    name: "Administración",
    description: "Reportes de negocio y su exportación.",
    permissions: [
      ...DASHBOARD_BASE,
      "reports:business_view",
      "reports:business_export",
    ],
  },
  {
    id: "laboratory",
    name: "Laboratorio",
    description: "Acceso al módulo de laboratorio.",
    permissions: [...DASHBOARD_BASE, "laboratory:view"],
  },
  {
    id: "bionalista",
    name: "Bioanalista",
    description:
      "Acceso al módulo de bioanálisis y al panel simplificado. El identificador conserva la errata original para no romper los datos existentes.",
    permissions: [...DASHBOARD_BASE, "bioanalysis:view"],
  },
  {
    id: "deploy",
    name: "Despliegue",
    description:
      "Descarga de instalaciones y emisión de enlaces de instalación. Por sí solo no da acceso al listado de usuarios, igual que antes.",
    permissions: [...DASHBOARD_BASE, "installations:*"],
  },
] as const satisfies readonly SystemRoleDef[];

const SYSTEM_ROLE_ID_SET: ReadonlySet<string> = new Set(
  SYSTEM_ROLES.map((role) => role.id)
);

/** ¿Este id corresponde a un rol del sistema (y por tanto no se puede borrar)? */
export function isSystemRoleId(id: string): id is LegacyRoleId {
  return SYSTEM_ROLE_ID_SET.has(id);
}

/**
 * El rol que garantiza que nunca se pierda la capacidad de administrar roles.
 * Su permiso global es inmutable, ver `utils/policy.ts`.
 */
export const BACKSTOP_ROLE_ID = "admin" as const satisfies LegacyRoleId;
